import { withRouter, useHistory } from 'react-router'
import { destroyCookie, setCookie } from 'nookies'
import { Dialog, Transition } from '@headlessui/react'
import { useDispatch, useSelector } from 'react-redux'
import { setUser } from 'state/redux/actions/user.actions'
import {
  SearchIcon,
  BanIcon,
  MenuAlt2Icon,
  XIcon,
  HomeIcon,
  ChartBarIcon,
  FilterIcon,
  ViewGridIcon,
  SwitchHorizontalIcon,
  ClipboardListIcon,
  CalendarIcon,
  UserGroupIcon,
  CashIcon,
  ShieldCheckIcon,
  OfficeBuildingIcon,
  MapIcon,
  TruckIcon,
  TagIcon,
  CloudUploadIcon,
  ChartPieIcon,
  SparklesIcon,
  DatabaseIcon,
  GlobeAltIcon,
  // ReceiptRefundIcon,
  DocumentDuplicateIcon,
  DocumentReportIcon,
  BriefcaseIcon,
  LinkIcon,
  MailIcon,
} from '@heroicons/react/outline'
import { TbRadar2 } from 'react-icons/tb'
import { Fragment, useState, useEffect, useMemo, useCallback } from 'react'
import { get, toLower, includes, split, flatMap, map, filter, trim, isArray } from 'lodash'
import classNames from 'classnames'
import logo from 'assets/images/ajarlee.png'
import { setDrawerToggle, setCompanyBranchFilter } from 'state/redux/actions/settings.actions'
import BottomMenu from 'containers/BottomMenu'
import LanguageSwitcher from 'components/atoms/LanguageSwitcher'
import { useTranslation } from 'react-i18next'
import apiGet from 'lib/network/apiGet'
import Selection from 'components/molecules/Selection'
import { getDashboardAccessPaths, hasAdminRole, isCompanyDashboardUser, isPartnerDashboardUser, usesTenantDashboardUi } from 'lib/auth/dashboardRole'

/** Expand / collapse + mobile close — matches sidebar chrome (soft slate, not solid dark block) */
const sidebarToggleButtonClass =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 text-slate-600 ring-1 ring-slate-200/80 transition-colors hover:from-slate-50 hover:to-slate-100 hover:text-slate-800 hover:ring-slate-300/90 focus:outline-none focus:ring-2 focus:ring-dark-500/30 focus:ring-offset-1 active:scale-[0.98]'

const matchesSearchText = (text, query) => includes(toLower(text), toLower(query))

const MenuChevron = ({ expanded, isRTL }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={classNames('h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform duration-200', expanded ? 'rotate-90' : '', isRTL && !expanded && '-scale-x-100')}
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden
  >
    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
  </svg>
)

const SidebarMenuLink = ({ link, pathname, isRTL, onNavigate }) => {
  const current = split(link.pathname, '?')[0] === pathname

  return (
    <button
      type="button"
      onClick={() => onNavigate(link.pathname)}
      className={classNames(
        'group flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-sm font-medium normal-case tracking-normal transition-all duration-200',
        current
          ? 'bg-dark-600/[0.09] text-dark-800 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.5)] ring-1 ring-dark-600/25'
          : 'text-slate-600 hover:bg-slate-50/90 hover:text-dark-800',
        isRTL && 'flex-row-reverse text-right'
      )}
      aria-current={current ? 'page' : undefined}
    >
      <span
        className={classNames(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors duration-200',
          current
            ? 'bg-dark-600 text-white shadow-sm ring-1 ring-dark-600/40'
            : 'bg-slate-100/90 text-slate-400 group-hover:bg-white group-hover:text-dark-600 group-hover:shadow-sm group-hover:ring-1 group-hover:ring-slate-200/80'
        )}
      >
        <link.icon className="h-5 w-5" strokeWidth={1.75} />
      </span>
      <span className="min-w-0 flex-1 leading-snug">{link.name}</span>
    </button>
  )
}

const SidebarMenuGroup = ({ group, pathname, isRTL, search, forceExpand, onNavigate }) => {
  // eslint-disable-next-line
  const hasActiveChild = group.items?.some(sub => split(sub.pathname, '?')[0] === pathname)
  const groupMatchesSearch = search !== '' && matchesSearchText(group.name, search)

  const visibleItems =
    group.items?.filter(link => {
      if (search === '') return true
      if (groupMatchesSearch) return true
      return matchesSearchText(link.name, search)
    }) || []

  const [open, setOpen] = useState(true)

  useEffect(() => {
    if (hasActiveChild) setOpen(true)
  }, [hasActiveChild, pathname])

  useEffect(() => {
    if (forceExpand) setOpen(true)
  }, [forceExpand])

  if (visibleItems.length === 0) return null

  const panelOpen = open || forceExpand

  return (
    <div className="pt-2 first:pt-0">
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className={classNames(
          'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors hover:bg-slate-50/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-dark-500/30',
          hasActiveChild ? 'text-dark-700' : 'text-slate-500',
          isRTL && 'flex-row-reverse text-right'
        )}
        aria-expanded={panelOpen}
      >
        <MenuChevron expanded={panelOpen} isRTL={isRTL} />
        <span className="min-w-0 flex-1 text-[11px] font-semibold uppercase tracking-[0.14em]">{group.name}</span>
      </button>
      <Transition
        show={panelOpen}
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 max-h-0"
        enterTo="opacity-100 max-h-[2000px]"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 max-h-[2000px]"
        leaveTo="opacity-0 max-h-0"
      >
        <div className="space-y-1 overflow-hidden px-1 pb-1 pt-0.5">
          {visibleItems.map((link, index) => (
            <SidebarMenuLink key={`${link.pathname}-${index}`} link={link} pathname={pathname} isRTL={isRTL} onNavigate={onNavigate} />
          ))}
        </div>
      </Transition>
    </div>
  )
}

const SidebarNav = ({ menu, search, forceExpand, pathname, isRTL, onNavigate, noResultsLabel }) => {
  const filteredMenu =
    menu?.filter(group => {
      if (search === '') return true
      // eslint-disable-next-line
      const childMatch = group.items?.some(item => matchesSearchText(item.name, search))
      return childMatch || matchesSearchText(group.name, search)
    }) || []

  const showEmptyState = trim(search) !== '' && filteredMenu.length === 0

  return (
    <nav className="mt-5">
      <div className="pb-4">
        <div className="mt-1 space-y-0.5 px-1">
          {showEmptyState ? (
            <p className="py-8 text-center text-xs text-slate-500">{noResultsLabel}</p>
          ) : (
            filteredMenu.map((group, index) => (
              <SidebarMenuGroup key={`${group.name}-${index}`} group={group} pathname={pathname} isRTL={isRTL} search={search} forceExpand={forceExpand} onNavigate={onNavigate} />
            ))
          )}
        </div>
      </div>
    </nav>
  )
}

const Sidebar = ({ setVisible, visible, user, openChangePasswordModal }) => {
  const [search, setSearch] = useState('')
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'

  const dispatch = useDispatch()
  const history = useHistory()
  const isBookingsPage = history.location.pathname === '/bookings'

  const drawer = useSelector(state => get(state, 'settings.drawer'))
  const userFromStore = useSelector(state => get(state, 'user.user'))
  const companyBranchFilterId = useSelector(state => get(state, 'settings.companyBranchFilterId'))
  const [branchOptions, setBranchOptions] = useState([])
  const [branchesLoading, setBranchesLoading] = useState(false)
  // eslint-disable-next-line
  const hasAdminAccess = hasAdminRole(userFromStore)
  const isCompanyRole = isCompanyDashboardUser(userFromStore)
  const isPartnerRole = isPartnerDashboardUser(userFromStore)
  const access = getDashboardAccessPaths(userFromStore)
  const hasAccessLoaded = isArray(access) && access.length > 0
  // Users linked to more than one branch get the same branch filter as super-company users.
  const isMultiBranchUser = isArray(userFromStore?.branch) && userFromStore.branch.length > 1
  const canFilterByBranch = Boolean(userFromStore?.superCompanyUser || isMultiBranchUser)

  const companyLogo = userFromStore?.company?.logoUrl
  const partnerLogo = userFromStore?.partner?.avatar
  const tenantLogo = companyLogo || partnerLogo
  const displayLogo = usesTenantDashboardUi(userFromStore) && tenantLogo ? tenantLogo : logo

  const handleLogout = e => {
    dispatch(setUser({}))

    destroyCookie({}, 'accessToken', { path: '/' })
    destroyCookie({}, 'companyBranchFilterId', { path: '/' })
    destroyCookie({}, 'dashboardRole', { path: '/' })
    dispatch(setCompanyBranchFilter(null))

    history.push('/login')
    return false
  }

  useEffect(() => {
    if (!canFilterByBranch) return undefined
    let cancelled = false
    setBranchesLoading(true)
    apiGet('branches', { pageSize: 1000 })
      .then(res => {
        if (cancelled || !res?.data?.branch) {
          setBranchOptions([])
          return
        }
        setBranchOptions(
          res.data.branch.map(b => ({
            id: b._id,
            name: b.name,
          }))
        )
      })
      .catch(() => {
        if (!cancelled) setBranchOptions([])
      })
      .finally(() => {
        if (!cancelled) setBranchesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [canFilterByBranch])

  const branchListForSelection = useMemo(() => [{ id: '', name: t('sidebar.branchFilterAll') }, ...branchOptions], [branchOptions, t])

  const handleBranchSelection = useCallback(
    item => {
      if (!item || item.id === '') {
        destroyCookie({}, 'companyBranchFilterId', { path: '/' })
        dispatch(setCompanyBranchFilter(null))
      } else {
        setCookie({}, 'companyBranchFilterId', item.id, { path: '/', maxAge: 60 * 60 * 24 * 365 })
        dispatch(setCompanyBranchFilter(item.id))
      }
    },
    [dispatch]
  )

  const branchFilterBlock =
    canFilterByBranch && !isPartnerRole ? (
      <div className={classNames('relative z-[70] border-b border-slate-100/80 px-3 pb-3 pt-3', isRTL && 'text-right')} id="sidebar-branch-filter-section">
        <div className={classNames('mb-2 flex items-center gap-2.5', isRTL && 'flex-row-reverse')}>
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-slate-100 to-slate-50 text-slate-600 ring-1 ring-slate-200/80"
            aria-hidden
          >
            <MapIcon className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{t('sidebar.branchFilterLabel')}</p>
            <p className="truncate text-xs text-slate-400">{t('sidebar.branchFilterHint')}</p>
          </div>
        </div>
        <Selection
          value={companyBranchFilterId ?? ''}
          onChange={handleBranchSelection}
          list={branchListForSelection}
          title={t('sidebar.branchFilterAll')}
          disabled={branchesLoading}
          showSearch={branchOptions.length > 8}
          searchPlaceholder={t('common.search')}
          height="240px"
          className="w-full max-w-none [&_.bg-white]:rounded-xl [&_.bg-white]:border-slate-200/90 [&_.bg-white]:shadow-sm [&_.bg-white]:ring-1 [&_.bg-white]:ring-slate-900/[0.04]"
        />
      </div>
    ) : null

  const filterMenuItemByAccess = useCallback(
    item => {
      if (hasAdminAccess && !hasAccessLoaded) {
        if (item.pathname === '/company-dashboard') return false
        if (item.pathname === '/office-financial-report') return false
        if (item.pathname === '/office-sales-report') return false
        return true
      }
      if (item.pathname === '/company-dashboard') {
        return isCompanyRole
      }
      if (item.pathname === '/office-financial-report') {
        return isCompanyRole
      }
      if (item.pathname === '/office-sales-report') {
        return isCompanyRole
      }
      return includes(access, item.pathname)
    },
    [access, hasAdminAccess, hasAccessLoaded, isCompanyRole]
  )

  const menuStructure = useMemo(
    () => [
      {
        name: t('sidebar.groups.overview'),
        icon: HomeIcon,
        items: [
          { name: t('sidebar.dashboard'), icon: HomeIcon, pathname: '/dashboard' },
          { name: t('sidebar.statsDashboard'), icon: ChartBarIcon, pathname: '/stats-dashboard' },
          { name: t('sidebar.googleAnalytics'), icon: ChartBarIcon, pathname: '/google-analytics' },
          { name: t('sidebar.bookingFunnel'), icon: FilterIcon, pathname: '/booking-funnel' },
          { name: t('sidebar.companyDashboard'), icon: ViewGridIcon, pathname: '/company-dashboard' },
          { name: t('sidebar.officeFinancialReport'), icon: CashIcon, pathname: '/office-financial-report' },
          { name: t('sidebar.officeSalesReport'), icon: DocumentReportIcon, pathname: '/office-sales-report' },
          ...(hasAdminAccess ? [{ name: t('sidebar.radar'), icon: TbRadar2, pathname: '/radar' }] : []),
        ],
      },
      {
        name: t('sidebar.groups.bookings'),
        items: [
          { name: t('sidebar.cars'), icon: TruckIcon, pathname: '/cars' },
          { name: t('sidebar.bookings'), icon: CalendarIcon, pathname: '/bookings' },
          { name: t('sidebar.preBookings'), icon: ClipboardListIcon, pathname: '/pre-bookings' },
          { name: t('sidebar.officeEvaluationReports'), icon: ClipboardListIcon, pathname: '/office-evaluation-reports' },
          { name: t('sidebar.transactions'), icon: SwitchHorizontalIcon, pathname: '/transactions' },
          { name: t('sidebar.bookingInvoices'), icon: DocumentDuplicateIcon, pathname: '/booking-invoices' },
          { name: t('sidebar.walletPayoutRequests'), icon: CashIcon, pathname: '/wallet-payout-requests' },
        ],
      },
      {
        name: t('sidebar.groups.company'),
        items: [
          { name: t('sidebar.companies'), icon: OfficeBuildingIcon, pathname: '/companies' },
          { name: t('sidebar.branches'), icon: MapIcon, pathname: '/branches' },
          { name: t('sidebar.partners'), icon: GlobeAltIcon, pathname: '/partners' },
          { name: t('sidebar.users'), icon: UserGroupIcon, pathname: '/users' },
          { name: t('sidebar.importsManagement'), icon: CloudUploadIcon, pathname: '/imports' },
          { name: t('sidebar.discounts'), icon: TagIcon, pathname: '/discounts' },
          { name: t('sidebar.branchDiscounts'), icon: SparklesIcon, pathname: '/branch-discounts' },
        ],
      },
      {
        name: t('sidebar.groups.access'),
        items: [
          { name: t('sidebar.roles'), icon: ShieldCheckIcon, pathname: '/roles' },
          ...(hasAdminAccess || userFromStore?.superCompanyUser ? [{ name: t('sidebar.blackList'), icon: BanIcon, pathname: '/fraud-users' }] : []),
          ...(hasAdminAccess ? [{ name: t('sidebar.shortLinks'), icon: LinkIcon, pathname: '/short-links' }] : []),
        ],
      },
      {
        name: t('sidebar.groups.reports'),
        items: [
          { name: t('sidebar.reports'), icon: ChartPieIcon, pathname: '/salesReport' },
          { name: t('sidebar.auditLogs'), icon: ClipboardListIcon, pathname: '/audits' },
        ],
      },
      {
        name: t('sidebar.groups.content'),
        items: [
          { name: t('sidebar.blogs'), icon: DocumentReportIcon, pathname: '/blogs' },
          { name: t('sidebar.newsletterSubscriptions'), icon: MailIcon, pathname: '/newsletter-subscriptions' },
          { name: t('sidebar.crmLeads'), icon: BriefcaseIcon, pathname: '/crm/leads' },
          // { name: t('sidebar.preBookings'), icon: ClipboardListIcon, pathname: '/pre-bookings' },
        ],
      },
      {
        name: t('sidebar.groups.settings'),
        items: [
          { name: t('sidebar.masterData'), icon: DatabaseIcon, pathname: '/master-data' },
          // Refund engine moved to Master Data → Refund Engine
          // { name: t('sidebar.refundPolicy'), icon: ReceiptRefundIcon, pathname: '/refund-policy' },
          ...(hasAdminAccess ? [{ name: t('sidebar.sortingEngine'), icon: SparklesIcon, pathname: '/sorting-engine' }] : []),
        ],
      },
    ],
    [t, hasAdminAccess, userFromStore?.superCompanyUser]
  )

  const menu = useMemo(
    () =>
      menuStructure
        .map(group => ({
          ...group,
          items: filter(group.items, filterMenuItemByAccess),
        }))
        .filter(group => group.items.length > 0),
    [menuStructure, filterMenuItemByAccess]
  )
  const forceExpandMenuGroups = trim(search) !== ''

  const handleDrawer = () => dispatch(setDrawerToggle(!drawer))

  const searchInputClass =
    'block w-full rounded-2xl border border-slate-200/80 bg-white/95 py-2.5 text-sm text-dark-800 shadow-sm placeholder:text-slate-400 transition focus:border-dark-600/40 focus:outline-none focus:ring-2 focus:ring-dark-500/25'

  const handleNavigate = useCallback(pathname => history.push(pathname), [history])

  const sidebarNav = (
    <SidebarNav
      menu={menu}
      search={search}
      forceExpand={forceExpandMenuGroups}
      pathname={history.location.pathname}
      isRTL={isRTL}
      onNavigate={handleNavigate}
      noResultsLabel={t('sidebar.noResults')}
    />
  )

  const sidebarShell = (
    <>
      <div
        className={classNames(
          'relative z-[100] flex flex-shrink-0 items-center justify-between gap-3 border-b border-slate-200/70 bg-white/90 px-4 pb-4 pt-3 shadow-[inset_0_-1px_0_0_rgba(254,85,121,0.08)] backdrop-blur-md',
          isRTL && 'flex-row-reverse'
        )}
      >
        <img className="h-9 w-auto min-w-0 max-w-[10rem] object-contain object-left" src={displayLogo} alt="" />
        <div className={classNames('flex flex-shrink-0 items-center gap-0.5', isRTL && 'flex-row-reverse')}>
          <LanguageSwitcher compact />
          <button type="button" onClick={handleDrawer} className={sidebarToggleButtonClass} aria-label="Toggle sidebar">
            <MenuAlt2Icon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="noscrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {branchFilterBlock}
          <div className="px-3 pt-3">
            <label htmlFor="sidebar-search" className="sr-only">
              Search
            </label>
            <div className="relative">
              <div className={`pointer-events-none absolute inset-y-0 ${isRTL ? 'right-0 pr-3.5' : 'left-0 pl-3.5'} flex items-center`} aria-hidden="true">
                <SearchIcon className="h-4 w-4 text-dark-600/80" />
              </div>
              <input
                id="sidebar-search"
                onChange={e => {
                  setSearch(e.target.value)
                }}
                value={search}
                type="search"
                name="search"
                className={classNames(searchInputClass, isRTL ? 'pr-10' : 'pl-10')}
                placeholder={t('sidebar.searchPlaceholder')}
                dir={isRTL ? 'rtl' : 'ltr'}
                autoComplete="off"
              />
            </div>
          </div>

          {sidebarNav}
        </div>

        <div className="relative z-10 mt-auto flex-shrink-0 border-t border-slate-200/70 bg-gradient-to-t from-white to-slate-50/40 pb-0 backdrop-blur-sm">
          <BottomMenu user={user} openChangePasswordModal={openChangePasswordModal} handleLogout={handleLogout} />
        </div>
      </div>
    </>
  )

  const sidebarChrome = classNames(
    'hidden bg-gradient-to-b from-white via-slate-50/40 to-slate-50/90 shadow-[4px_0_32px_-12px_rgba(37,36,61,0.12)]',
    isRTL
      ? isBookingsPage
        ? 'sidebar:border-l sidebar:border-slate-200/90'
        : 'lg:border-l lg:border-slate-200/90'
      : isBookingsPage
      ? 'sidebar:border-r sidebar:border-slate-200/90'
      : 'lg:border-r lg:border-slate-200/90'
  )

  return (
    <>
      <Transition.Root show={visible} as={Fragment}>
        <Dialog as="div" className={classNames('fixed inset-0 z-[11000] flex', isBookingsPage ? 'sidebar:hidden' : 'lg:hidden')} onClose={setVisible}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px]" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom={isRTL ? 'translate-x-full' : '-translate-x-full'}
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo={isRTL ? 'translate-x-full' : '-translate-x-full'}
          >
            <div
              className={classNames(
                'relative flex max-h-[100dvh] w-full max-w-[min(20rem,100vw)] flex-col bg-gradient-to-b from-white via-slate-50/30 to-slate-50/90 pt-4 pb-0 shadow-2xl ring-1 ring-slate-900/5',
                isRTL ? 'text-right' : 'text-left'
              )}
            >
              <div
                className={classNames(
                  'flex flex-shrink-0 items-center justify-between gap-3 border-b border-slate-200/70 bg-white/80 px-4 pb-3 pt-1 shadow-[inset_0_-1px_0_0_rgba(254,85,121,0.08)] backdrop-blur-md',
                  isRTL && 'flex-row-reverse'
                )}
              >
                <img className="h-9 w-auto" src={displayLogo} alt="" />
                <button type="button" className={sidebarToggleButtonClass} onClick={() => setVisible(false)}>
                  <span className="sr-only">Close sidebar</span>
                  <XIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <div className="noscrollbar min-h-0 flex-1 overflow-y-auto">
                  {branchFilterBlock}
                  <div className="px-3 pt-3">
                    <label htmlFor="sidebar-search-mobile" className="sr-only">
                      Search
                    </label>
                    <div className="relative">
                      <div className={`pointer-events-none absolute inset-y-0 ${isRTL ? 'right-0 pr-3.5' : 'left-0 pl-3.5'} flex items-center`} aria-hidden="true">
                        <SearchIcon className="h-4 w-4 text-dark-600/80" />
                      </div>
                      <input
                        id="sidebar-search-mobile"
                        onChange={e => setSearch(e.target.value)}
                        value={search}
                        type="search"
                        className={classNames(searchInputClass, isRTL ? 'pr-10' : 'pl-10')}
                        placeholder={t('sidebar.searchPlaceholder')}
                        dir={isRTL ? 'rtl' : 'ltr'}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  {sidebarNav}
                </div>
                <div className="relative z-10 mt-auto flex-shrink-0 border-t border-slate-200/70 bg-gradient-to-t from-white to-slate-50/40 pb-0 backdrop-blur-sm">
                  <BottomMenu user={user} openChangePasswordModal={openChangePasswordModal} handleLogout={handleLogout} />
                </div>
              </div>
            </div>
          </Transition.Child>
          <div className="w-14 flex-shrink-0" aria-hidden="true" />
        </Dialog>
      </Transition.Root>
      <div
        className={classNames(
          drawer ? 'noscrollbar drawer-open' : 'drawer-close opacity-0',
          sidebarChrome,
          isBookingsPage
            ? 'sidebar:flex sidebar:min-h-0 sidebar:flex-col sidebar:fixed sidebar:inset-y-0 sidebar:z-[40] sidebar:w-64 sidebar:pt-4 sidebar:pb-0'
            : 'lg:z-[30] lg:flex lg:min-h-0 lg:flex-col lg:fixed lg:inset-y-0 lg:w-64 lg:pt-4 lg:pb-0'
        )}
      >
        {sidebarShell}
      </div>
    </>
  )
}

export default withRouter(Sidebar)
